/*
 * Pricing in Sanity is authored as free text ("PKR 59,600"), so the currency the
 * amount is written in travels with the string rather than living in its own field.
 */

// Assumed when a `pricing` string carries no currency marker at all.
export const BASE_CURRENCY = 'PKR';

export const DEFAULT_CURRENCY = 'USD';

export const CURRENCIES = [
	{ code: 'USD', name: 'US Dollar' },
	{ code: 'PKR', name: 'Pakistani Rupee' },
	{ code: 'EUR', name: 'Euro' },
	{ code: 'GBP', name: 'British Pound' },
];

export const CURRENCY_CODES = CURRENCIES.map(({ code }) => code);

const SYMBOL_CODES = { $: 'USD', '€': 'EUR', '£': 'GBP', '₨': 'PKR', Rs: 'PKR' };

// Keeps a converted price off numbers like "PKR 38,142".
const ROUNDING_STEP = { USD: 1, EUR: 1, GBP: 1, PKR: 100 };

/**
 * Pulls an amount and its currency out of an authored price string.
 * Returns null for copy that isn't a price at all ("Custom quote").
 */
export const parsePrice = (raw) => {
	if (typeof raw !== 'string') return null;

	const text = raw.trim();
	const amount = Number.parseFloat(text.replace(/[^0-9.,]/g, '').replace(/,/g, ''));
	if (!Number.isFinite(amount)) return null;

	const code = CURRENCY_CODES.find((c) => new RegExp(`\\b${c}\\b`, 'i').test(text));
	const symbol = Object.keys(SYMBOL_CODES).find((s) => text.includes(s));

	return { amount, currency: code || SYMBOL_CODES[symbol] || BASE_CURRENCY };
};

/** Rates are quoted per 1 USD, so cross-rates route through USD. */
export const convert = (amount, from, to, rates) => {
	if (from === to) return amount;
	if (!rates?.[from] || !rates?.[to]) return null;

	return (amount / rates[from]) * rates[to];
};

export const roundPrice = (amount, code) => {
	const step = ROUNDING_STEP[code] || 1;
	return Math.round(amount / step) * step;
};

const priceFormatters = new Map();

export const formatPrice = (amount, code) => {
	if (!priceFormatters.has(code)) {
		priceFormatters.set(
			code,
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: code,
				currencyDisplay: 'symbol',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			})
		);
	}

	return priceFormatters.get(code).format(amount);
};

const rateFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** Reads as "1 USD = 278.23 PKR". */
export const formatRate = (code, rates) => {
	const rate = convert(1, code, BASE_CURRENCY, rates);
	if (rate === null) return null;

	return `1 ${code} = ${rateFormatter.format(rate)} ${BASE_CURRENCY}`;
};

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
	day: 'numeric',
	month: 'short',
	year: 'numeric',
});

export const formatRateDate = (timestamp) =>
	Number.isFinite(timestamp) ? dateFormatter.format(new Date(timestamp)) : null;
